'use client'

import type { StudioDay, StudioWeekday } from '@repo/core/studio-calendar'
import { clockLabel } from '@repo/db/office-hours'
import { ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  DAY_LONG,
  MONTH,
  MONTH_OF,
  rowBasis,
  rowColumns,
  startsFor,
} from '@/components/sections/studio/studio-dates'
import { fromISO, toISO } from '@/lib/dates'
import { KICKER } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * „Wszystkie terminy" — every bookable day of the room, a month at a time.
 *
 * The panel on the page shows six free days, which is the right number for the
 * common case and the wrong one for anybody planning further out. Owner: *„nie
 * można wybrać terminu na za 2 tygodnie, bo jest tylko 6 dat — jakaś opcja,
 * pokaż cały kalendarz albo coś w tym stylu"*; the first cut was „basicowy", so
 * this one says more per square:
 *
 * - every day carries its opening hours under the number („8–22"), or why it
 *   cannot be booked („zamkn.", „zajęte"), so the month reads at a glance;
 * - four shortcuts — the soonest day, a week out, two weeks, a month — for the
 *   questions people actually ask („a za dwa tygodnie?");
 * - the chosen day is previewed beside the grid with its own start times, so
 *   the day AND the hour are settled here and the dialog closes on one button.
 *
 * Nothing here is invented: open days are the ones the page computed from the
 * published calendar minus reservations, and the hours come from the published
 * week. On a phone it is a sheet from the bottom, one column.
 */
export function StudioDatesDialog({
  onClose,
  onPick,
  roomName,
  calendar,
  week,
  hours,
  openDays,
  value,
  time,
  hoursNote,
}: {
  onClose: () => void
  onPick: (day: string, time: string | null) => void
  roomName: string
  calendar: readonly StudioDay[]
  week: readonly StudioWeekday[]
  hours: number
  openDays: readonly string[]
  value: string | null
  time: string | null
  hoursNote?: string | undefined
}) {
  const [month, setMonth] = useState<Date>(() => monthOf(value ?? openDays[0] ?? null))
  const [pending, setPending] = useState<string | null>(value)
  const [pendingTime, setPendingTime] = useState<string | null>(time)
  const panelRef = useRef<HTMLDivElement>(null)
  // The page re-renders while this is open; reading the latest `onClose`
  // through a ref keeps the effect below from re-running on every one of them.
  const closeHandler = useRef(onClose)
  closeHandler.current = onClose

  // Mounted only while open (the page renders it conditionally), so the state
  // above starts from what the page has picked every time it opens.
  useEffect(() => {
    panelRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeHandler.current()
    }
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = overflow
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  if (typeof document === 'undefined') return null

  const openSet = new Set(openDays)
  const today = toISO(new Date())
  const firstMonth = monthOf(openDays[0] ?? today)
  const lastMonth = monthOf(calendar.at(-1)?.date ?? openDays.at(-1) ?? today)
  const canBack = month > firstMonth
  const canNext = month < lastMonth
  const shift = (by: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + by, 1))

  const choose = (iso: string) => {
    setPending(iso)
    setPendingTime(null)
    setMonth(monthOf(iso))
  }

  // ── THE GRID ────────────────────────────────────────────────────────
  const lead = (month.getDay() + 6) % 7
  const length = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const firstBookable = calendar[0]?.date ?? today
  const cells = Array.from({ length }, (_, index) => {
    const date = new Date(month.getFullYear(), month.getMonth(), index + 1)
    const iso = toISO(date)
    const published = week.find((day) => day.weekday === date.getDay())
    const bookable = openSet.has(iso)
    let note = ''
    if (iso === today) note = 'dziś'
    else if (iso < firstBookable) note = ''
    else if (bookable && published)
      note = `${hourOnly(published.opensAt)}–${hourOnly(published.closesAt)}`
    else if (!published) note = 'zamkn.'
    else if (iso <= (calendar.at(-1)?.date ?? '')) note = 'zajęte'
    return { iso, day: index + 1, bookable, note, isToday: iso === today }
  })

  // ── SHORTCUTS ───────────────────────────────────────────────────────
  const firstFrom = (iso: string) => openDays.find((day) => day >= iso) ?? null
  const plus = (days: number) => {
    const date = fromISO(today) ?? new Date()
    date.setDate(date.getDate() + days)
    return toISO(date)
  }
  const quick = [
    { label: 'Najbliższy', day: openDays[0] ?? null },
    { label: 'Za tydzień', day: firstFrom(plus(7)) },
    { label: 'Za 2 tygodnie', day: firstFrom(plus(14)) },
    { label: 'Za miesiąc', day: firstFrom(plus(28)) },
  ].filter((entry, index, all) => entry.day && all.findIndex((x) => x.day === entry.day) === index)

  // ── THE PICKED DAY ──────────────────────────────────────────────────
  const pendingDate = pending ? fromISO(pending) : null
  const published = pendingDate ? week.find((day) => day.weekday === pendingDate.getDay()) : null
  const starts = startsFor(calendar, pending, hours)
  const start = pendingTime && starts.includes(pendingTime) ? pendingTime : (starts[0] ?? null)

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="studio-dates-title"
      className="fixed inset-0 z-[60] flex items-end justify-center p-2.5 sm:items-center sm:p-6"
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Zamknij"
        onClick={onClose}
        className="flou-modal-backdrop absolute inset-0 cursor-pointer bg-[oklch(0.04_0.01_280/0.72)] backdrop-blur-[14px]"
        style={{
          backgroundImage:
            'radial-gradient(60% 50% at 50% 40%, color-mix(in oklab, var(--studio-accent, #c026d3) 18%, transparent), transparent 70%)',
        }}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className="flou-modal-card relative outline-none grid max-h-[calc(100svh-1.25rem)] w-full max-w-[52rem] grid-cols-1 overflow-y-auto rounded-[1.75rem] border border-white/[0.12] bg-[oklch(0.12_0.01_280/0.96)] shadow-[0_50px_140px_-40px_var(--studio-glow,rgb(0_0_0/0.8))] md:grid-cols-[minmax(0,1fr)_16.25rem] md:overflow-hidden"
      >
        {/* ── MONTH ───────────────────────────────────────────── */}
        <div className="p-4 sm:p-6">
          <div className="mb-4 flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <p className={cn(KICKER, 'text-white/50')}>Wybierz dzień · {roomName}</p>
              <h2
                id="studio-dates-title"
                className="mt-1.5 font-masthead text-[1.75rem] font-bold capitalize leading-none tracking-[-0.03em] text-white sm:text-[2rem]"
              >
                {MONTH[month.getMonth()]}{' '}
                <span className="font-semibold text-white/50">{month.getFullYear()}</span>
              </h2>
            </div>
            <button
              type="button"
              onClick={() => shift(-1)}
              disabled={!canBack}
              aria-label="Poprzedni miesiąc"
              className={ROUND}
            >
              <ChevronLeft size={16} strokeWidth={1.6} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => shift(1)}
              disabled={!canNext}
              aria-label="Następny miesiąc"
              className={ROUND}
            >
              <ChevronRight size={16} strokeWidth={1.6} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Zamknij"
              className={cn(ROUND, 'md:hidden')}
            >
              <X size={16} strokeWidth={1.6} aria-hidden="true" />
            </button>
          </div>

          {quick.length > 0 ? (
            <div className="mb-4 flex flex-wrap gap-1.5">
              {quick.map((entry) => (
                <button
                  key={entry.label}
                  type="button"
                  onClick={() => entry.day && choose(entry.day)}
                  aria-pressed={entry.day === pending}
                  className={cn(
                    'min-h-[2.125rem] rounded-full border px-3.5 text-[0.8rem] font-semibold transition-colors',
                    entry.day === pending
                      ? 'border-white bg-white text-neutral-900'
                      : 'border-white/[0.14] bg-white/[0.03] text-white/85 hover:border-white/35',
                  )}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          ) : null}

          <div aria-hidden="true" className="mb-1.5 grid grid-cols-7 gap-1.5">
            {['pn', 'wt', 'śr', 'czw', 'pt', 'sob', 'nd'].map((label) => (
              <span
                key={label}
                className="text-center text-[0.66rem] font-semibold uppercase tracking-[0.12em] text-white/50"
              >
                {label}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {Array.from({ length: lead }, (_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: blank cells have no identity
              <span key={`blank-${index}`} aria-hidden="true" />
            ))}
            {cells.map((cell) => {
              const on = cell.iso === pending
              const date = fromISO(cell.iso)
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={!cell.bookable}
                  onClick={() => choose(cell.iso)}
                  aria-pressed={on}
                  aria-label={`${date ? DAY_LONG[date.getDay()] : ''} ${cell.day} ${MONTH_OF[month.getMonth()]}${cell.bookable ? '' : ', niedostępny'}`}
                  className={cn(
                    'flex h-[2.9rem] flex-col items-center justify-center gap-[3px] rounded-[0.7rem] border transition-[border-color,background-color,transform] duration-200 sm:h-[3.6rem] sm:rounded-[0.9rem]',
                    on
                      ? 'border-white bg-white text-neutral-900 shadow-[0_0_0_4px_color-mix(in_oklab,var(--studio-accent,#c026d3)_30%,transparent)]'
                      : cell.bookable
                        ? 'border-white/[0.08] bg-white/[0.035] text-white hover:-translate-y-px hover:border-white/35 hover:bg-white/[0.07]'
                        : 'cursor-default border-transparent text-white/20',
                  )}
                >
                  <b className="font-masthead text-[1rem] font-bold leading-none sm:text-[1.1rem]">
                    {cell.day}
                  </b>
                  {cell.note ? (
                    <small
                      className={cn(
                        'text-[0.55rem] leading-none sm:text-[0.62rem]',
                        on
                          ? 'text-neutral-900/60'
                          : cell.isToday
                            ? 'text-[color-mix(in_oklab,var(--studio-accent,#c026d3)_80%,white)]'
                            : cell.bookable
                              ? 'text-white/50'
                              : 'text-white/20',
                      )}
                    >
                      {cell.note}
                    </small>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>

        {/* ── THE PICKED DAY ──────────────────────────────────── */}
        <div
          className="flex flex-col gap-3.5 border-t border-white/10 p-4 sm:p-6 md:border-t-0 md:border-l"
          style={{
            background:
              'linear-gradient(180deg, color-mix(in oklab, var(--studio-accent, #c026d3) 10%, transparent), transparent 60%)',
          }}
        >
          <div className="-mt-1 hidden justify-end md:flex">
            <button type="button" onClick={onClose} aria-label="Zamknij" className={ROUND}>
              <X size={16} strokeWidth={1.6} aria-hidden="true" />
            </button>
          </div>

          {pendingDate ? (
            <>
              <div className="flex items-end gap-4 md:block">
                <p className="bg-[image:var(--studio-grad-text,var(--flou-gradient-text))] bg-clip-text font-masthead text-[3.25rem] font-bold leading-[0.9] tracking-[-0.04em] text-transparent md:mt-2 md:text-[4rem]">
                  {pendingDate.getDate()}
                </p>
                <div>
                  <p className={cn(KICKER, 'text-white/50')}>{DAY_LONG[pendingDate.getDay()]}</p>
                  <p className="mt-1 font-masthead text-[1.15rem] font-semibold text-white">
                    {MONTH_OF[pendingDate.getMonth()]} {pendingDate.getFullYear()}
                  </p>
                  {published ? (
                    <p className="mt-1 text-[0.8rem] text-white/50">
                      Studio otwarte {clockLabel(published.opensAt)}–
                      {clockLabel(published.closesAt)}
                    </p>
                  ) : null}
                </div>
              </div>

              {starts.length > 0 ? (
                <div>
                  <p className={cn(KICKER, 'mb-2 text-white/50')}>Godzina rozpoczęcia</p>
                  <div className="flex flex-wrap gap-1.5">
                    {starts.map((at) => (
                      <button
                        key={at}
                        type="button"
                        onClick={() => setPendingTime(at)}
                        aria-pressed={at === start}
                        style={rowBasis(rowColumns(starts.length, 3), 0.375)}
                        className={cn(
                          'min-h-10 min-w-0 grow whitespace-nowrap rounded-full border px-1 text-[0.88rem] font-semibold tabular-nums transition-colors',
                          at === start
                            ? 'border-white bg-white text-neutral-900'
                            : 'border-white/[0.14] text-white/85 hover:border-white/35',
                        )}
                      >
                        {at}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-[0.85rem] text-white/60">Wybierz dzień w kalendarzu.</p>
          )}

          <button
            type="button"
            disabled={!pending}
            onClick={() => pending && onPick(pending, start)}
            className="mt-auto inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white font-semibold text-neutral-900 transition-transform duration-300 hover:scale-[1.02] disabled:bg-white/10 disabled:text-white/50"
          >
            Wybierz termin
            <ArrowRight size={16} aria-hidden="true" />
          </button>
          {hoursNote ? (
            <p className="text-[0.72rem] leading-normal text-white/45">{hoursNote}</p>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}

const ROUND =
  'inline-flex size-[2.4rem] shrink-0 items-center justify-center rounded-full border border-white/[0.18] text-white transition-colors hover:border-white/50 hover:bg-white/[0.06] disabled:pointer-events-none disabled:opacity-25'

/** The first of the month a calendar day falls in. */
function monthOf(iso: string | null): Date {
  const date = (iso && fromISO(iso)) || new Date()
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/** „8" for 8:00, „8:30" for half past — the grid has no room for „:00". */
function hourOnly(minutes: number): string {
  return minutes % 60 === 0 ? String(minutes / 60) : clockLabel(minutes)
}
